// Liste des démarches de la collectivité : état, version publiée, version en préparation,
// dossiers (comptés sur les dossiers lisibles, 500 au plus), création d'une démarche.
import type { Demarche, Dossier } from '@cc/contrats';
import { Bandeau, Bouton, Champ, Dialogue, EtatVide, Filtres, GrilleChamps, Lien, PuceEtat, Selection, Tableau, useFiltresPersistes, ZoneTexte, type ColonneTableau } from '@cc/ui';
import { useQuery } from '@tanstack/react-query';
import { FileCog, Plus } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { requete } from '../../api/client';
import { EnTetePage } from '../../commun/EnTetePage';
import { EtatRequete } from '../../commun/EtatRequete';
import { date } from '../../commun/format';
import { useAction } from '../../commun/useAction';
import { useSession } from '../../session/FournisseurSession';
import { moduleActif, peut } from '../../session/permissions';
import { LIBELLES_DOMAINES_DEMARCHE, LIBELLES_ETAT_DEMARCHE, LIBELLES_ETAT_VERSION } from './contenu';
import commun from '../../commun/commun.module.css';

export function puceDemarche(etat: string) {
  const libelle = LIBELLES_ETAT_DEMARCHE[etat] ?? etat;
  if (etat === 'publiee') return <PuceEtat ton="ok" libelle={libelle} />;
  if (etat === 'en_verification') return <PuceEtat ton="attente" libelle={libelle} />;
  if (etat === 'suspendue' || etat === 'retiree') return <PuceEtat ton="retard" libelle={libelle} />;
  return <PuceEtat ton="neutre" libelle={libelle} />;
}

/** Dossiers lisibles par l'agent, pour les statistiques par démarche (500 derniers au plus). */
export function useStatistiquesDossiers() {
  return useQuery({
    queryKey: ['dossiers', 'statistiques'],
    queryFn: ({ signal }) => requete<{ elements: Dossier[]; suivant: string | null }>('/dossiers', { signal, parametres: { limite: 500 } }),
    retry: false,
    staleTime: 60_000,
  });
}

export function compterParDemarche(dossiers: Dossier[] | undefined): Map<string, { total: number; en_cours: number }> {
  const m = new Map<string, { total: number; en_cours: number }>();
  for (const d of dossiers ?? []) {
    const x = m.get(d.demarche.id) ?? { total: 0, en_cours: 0 };
    x.total += 1;
    if (!['cloture', 'archive', 'classe_sans_suite', 'retire'].includes(d.etat)) x.en_cours += 1;
    m.set(d.demarche.id, x);
  }
  return m;
}

function codeDepuis(libelle: string): string {
  return libelle
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^[^a-z]+|_+$/g, '')
    .slice(0, 40);
}

function NouvelleDemarche({ ouvert, onFermer }: { ouvert: boolean; onFermer: () => void }) {
  const navigate = useNavigate();
  const [s, setS] = useState({ code: '', libelle: '', description: '', domaine: 'dossier', autorite_responsable: '' });
  const creation = useAction({
    executer: () => requete<Demarche>('/demarches', { methode: 'POST', corps: { code: s.code, libelle: s.libelle, description: s.description || undefined, domaine: s.domaine, autorite_responsable: s.autorite_responsable || undefined } }),
    succes: (d) => `Démarche « ${d.libelle} » créée en brouillon (version 1).`,
    invalider: [['demarches']],
    apres: (d) => {
      onFermer();
      navigate(`/demarches/${d.id}/versions/1`);
    },
  });
  const maj = (cle: keyof typeof s) => (e: { target: { value: string } }) => setS((x) => ({ ...x, [cle]: e.target.value }));
  const erreur = (champ: string) => creation.erreursChamps.find((c) => c.champ === champ)?.message;
  return (
    <Dialogue
      ouvert={ouvert}
      titre="Nouvelle démarche"
      onFermer={onFermer}
      description="La démarche est créée en brouillon avec une première version à compléter dans l'éditeur."
      actions={
        <>
          <Bouton variante="secondaire" onClick={onFermer}>
            Annuler
          </Bouton>
          <Bouton chargement={creation.enCours} disabled={s.code.length < 2 || s.libelle.trim().length < 3} onClick={() => creation.lancer(undefined)}>
            Créer la démarche
          </Bouton>
        </>
      }
    >
      <GrilleChamps>
        <Champ id="nd-libelle" libelle="Libellé" value={s.libelle} required erreur={erreur('libelle')} onChange={(e) => setS((x) => ({ ...x, libelle: e.target.value, code: x.code && x.code !== codeDepuis(x.libelle) ? x.code : codeDepuis(e.target.value) }))} />
        <Champ id="nd-code" libelle="Code" className="cc-mono" value={s.code} required maxLength={40} aide="Minuscules, chiffres et soulignés ; il ne change plus ensuite." erreur={erreur('code')} onChange={(e) => setS((x) => ({ ...x, code: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_') }))} />
      </GrilleChamps>
      <Selection id="nd-domaine" libelle="Domaine" value={s.domaine} options={Object.entries(LIBELLES_DOMAINES_DEMARCHE).map(([valeur, libelle]) => ({ valeur, libelle }))} onChange={maj('domaine')} />
      <Champ id="nd-autorite" libelle="Autorité responsable" value={s.autorite_responsable} facultatif aide="Par défaut : le maire ou le président." onChange={maj('autorite_responsable')} />
      <ZoneTexte id="nd-description" libelle="Description" value={s.description} facultatif rows={3} onChange={maj('description')} />
      {creation.messageErreur ? <Bandeau type="erreur">{creation.messageErreur}</Bandeau> : null}
    </Dialogue>
  );
}

export function PageDemarches() {
  const { contexte } = useSession();
  const [filtres, setFiltres] = useFiltresPersistes('demarches', { etat: '' });
  const [creation, setCreation] = useState(false);
  const demarches = useQuery({
    queryKey: ['demarches', 'liste'],
    queryFn: ({ signal }) => requete<{ elements: Demarche[] }>('/demarches', { signal }).then((r) => r.elements),
    retry: false,
  });
  const stats = useStatistiquesDossiers();
  const compteurs = compterParDemarche(stats.data?.elements);
  const lignes = (demarches.data ?? []).filter((d) => !filtres.etat || d.etat === filtres.etat);

  const colonnes: ColonneTableau<Demarche>[] = [
    { id: 'libelle', entete: 'Démarche', enTeteLigne: true, masquable: false, valeurTri: (d) => d.libelle, cellule: (d) => <Lien href={`/demarches/${d.id}`}>{d.libelle}</Lien> },
    { id: 'code', entete: 'Code', cellule: (d) => <span className="cc-mono">{d.code}</span>, valeurTri: (d) => d.code },
    { id: 'domaine', entete: 'Domaine', cellule: (d) => LIBELLES_DOMAINES_DEMARCHE[d.domaine] ?? d.domaine, valeurTri: (d) => d.domaine },
    { id: 'etat', entete: 'État', cellule: (d) => puceDemarche(d.etat), valeurTri: (d) => d.etat },
    { id: 'courante', entete: 'Version publiée', cellule: (d) => (d.version_courante ? `Version ${d.version_courante.numero}, le ${date(d.version_courante.publiee_le)}` : 'aucune'), valeurTri: (d) => d.version_courante?.numero ?? null },
    { id: 'en_cours', entete: 'Version en préparation', cellule: (d) => (d.version_en_cours ? `Version ${d.version_en_cours.numero} (${LIBELLES_ETAT_VERSION[d.version_en_cours.etat] ?? d.version_en_cours.etat})` : 'aucune') },
    { id: 'dossiers', entete: 'Dossiers (en cours)', numerique: true, cellule: (d) => (stats.data ? `${compteurs.get(d.id)?.total ?? 0} (${compteurs.get(d.id)?.en_cours ?? 0})` : 'non lisible'), valeurTri: (d) => compteurs.get(d.id)?.total ?? 0 },
    { id: 'interne', entete: 'Nature', cellule: (d) => (d.interne ? 'Interne de test' : 'Éditeur') },
  ];

  return (
    <>
      <EnTetePage
        etiquette="Moteur de démarches"
        titre="Démarches"
        description="Démarches configurées sans code : chaque version publiée fixe le formulaire, les pièces, les étapes et les délais des dossiers déposés avec elle."
        actions={peut(contexte, 'demarche_config', 'creer') && moduleActif(contexte, 'M05') !== false ? <Bouton variante="principal" icone={Plus} onClick={() => setCreation(true)}>Nouvelle démarche</Bouton> : null}
      />
      <Filtres
        groupes={[{ id: 'etat', libelle: 'État', options: [{ valeur: '', libelle: 'Toutes' }, ...Object.entries(LIBELLES_ETAT_DEMARCHE).map(([valeur, libelle]) => ({ valeur, libelle }))] }]}
        valeurs={filtres}
        onChanger={setFiltres}
      />
      <EtatRequete chargement={demarches.isPending} erreur={demarches.error} onReessayer={() => void demarches.refetch()} lot="L1-A" fonction="L'éditeur de démarches">
        {lignes.length === 0 ? (
          <EtatVide titre="Aucune démarche" icone={FileCog}>
            <p>Aucune démarche ne correspond à ce filtre.</p>
          </EtatVide>
        ) : (
          <>
            <Tableau legende="Démarches" colonnes={colonnes} lignes={lignes} cleLigne={(d) => d.id} cleColonnes="demarches" />
            <p className={commun.na}>
              {stats.data
                ? `Dossiers comptés sur les ${stats.data.elements.length} dossiers les plus récents que vous pouvez lire${stats.data.suivant ? ' (il en existe d\'autres)' : ''}.`
                : 'Les dossiers ne sont pas comptés : la liste des dossiers n\'est pas lisible avec vos droits.'}
            </p>
          </>
        )}
      </EtatRequete>
      <NouvelleDemarche ouvert={creation} onFermer={() => setCreation(false)} />
    </>
  );
}
